import { createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";

import { SearchPage } from "@/features/directory/modules/search-page/search-page";
import { directoryCitiesRequest, loadSearchPage } from "@/features/directory/utils/load-search-page";
import { searchHead, searchTitle } from "@/features/directory/utils/search-head";
import { DIRECTORY_SORTS, directorySearchSchema } from "@/features/directory/utils/search-params";
import { cleanSearchPath } from "@/features/directory/utils/search-path";
import { localizePath } from "@/lib/locale";
import { getLocale } from "@/paraglide/runtime.js";

const searchPageSchema = directorySearchSchema.extend({
  sort: z.enum(DIRECTORY_SORTS).optional().catch(undefined),
});

export const Route = createFileRoute("/(public)/search/")({
  // Every search is a link: what, where and how sorted live in the query string.
  validateSearch: searchPageSchema,
  // It names its own canonical address (ADR 0042).
  staticData: { ownAlternates: true },
  loaderDeps: ({ search }) => search,
  loader: async ({ deps }) => {
    const { data } = await directoryCitiesRequest().send();
    const cities = data ?? [];
    // A plain service or city has an address of its own, which is the page search engines keep.
    const clean = cleanSearchPath(deps, cities);
    if (clean) throw redirect({ href: localizePath(clean, getLocale()), statusCode: 301 });
    return { crumb: searchTitle(deps), cities, ...(await loadSearchPage(deps)) };
  },
  head: ({ loaderData, match }) => searchHead({ search: match.search, total: loaderData?.result?.total ?? 0 }),
  component: SearchRoute,
});

function SearchRoute() {
  const search = Route.useSearch();
  const { result, firstTimes, cities } = Route.useLoaderData();
  return <SearchPage search={search} result={result} firstTimes={firstTimes} cities={cities} />;
}
